import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, Ticket as TicketIcon } from 'lucide-react';
import Modal from './Modal';
import { ApiService } from '../services/api';
import { Ticket } from '../types';
import { getErrorMessage } from '../services/supabaseClient';

type TicketSearchResult = Pick<
  Ticket,
  'id' | 'pcn_number' | 'vrn' | 'issuing_authority' | 'status' | 'issue_date'
>;

const isTypingTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  const tag = el?.tagName?.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    (el as any)?.isContentEditable
  );
};

export default function TicketSearch() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<TicketSearchResult[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      const isCmdK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (isCmdK) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults([]);
    setError(null);
  }, [open]);

  const normalizedQuery = useMemo(() => query.trim(), [query]);
  const searchDisabled = normalizedQuery.length < 2;

  useEffect(() => {
    if (!open) return;
    if (searchDisabled) {
      setResults([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const timer = window.setTimeout(() => {
      ApiService.searchTickets(normalizedQuery, 10)
        .then((data) => {
          if (cancelled) return;
          setResults(data);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(getErrorMessage(err));
          setResults([]);
        })
        .finally(() => {
          if (cancelled) return;
          setLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [normalizedQuery, open, searchDisabled]);

  const handleSelect = (ticketId: string) => {
    setOpen(false);
    navigate(`/tickets/${ticketId}`);
  };

  const hint = useMemo(() => {
    if (searchDisabled) return 'Type at least 2 characters';
    if (loading) return 'Searching…';
    if (error) return error;
    if (normalizedQuery && results.length === 0) return 'No tickets found';
    return null;
  }, [error, loading, normalizedQuery, results.length, searchDisabled]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-950/30 hover:bg-white dark:hover:bg-slate-900 transition-colors text-sm text-slate-600 dark:text-slate-300"
        title="Search tickets (Ctrl/⌘ + K)"
      >
        <Search size={16} className="text-slate-400" />
        <span className="min-w-0 truncate">Search tickets…</span>
        <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 bg-white/50 dark:bg-slate-900/40">
          Ctrl K
        </span>
      </button>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden p-2 rounded-full text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        aria-label="Search tickets"
        title="Search tickets"
      >
        <Search size={20} />
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Search tickets" maxWidth="max-w-2xl">
        <div className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by PCN number or VRN…"
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-950/30 dark:text-white"
            />
          </div>

          {hint && (
            <div className="mt-3 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <TicketIcon size={14} />}
              <span>{hint}</span>
            </div>
          )}

          <div className="mt-4 divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {results.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleSelect(t.id)}
                className="w-full text-left p-4 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 dark:text-white truncate">
                      {t.pcn_number} <span className="text-slate-400 dark:text-slate-500">•</span> {t.vrn}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 truncate">
                      {t.issuing_authority || 'Unknown Authority'}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-xs font-bold text-slate-700 dark:text-slate-300">{t.status}</div>
                    <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">
                      {t.issue_date ? new Date(t.issue_date).toLocaleDateString('en-GB') : ''}
                    </div>
                  </div>
                </div>
              </button>
            ))}

            {results.length === 0 && !hint && (
              <div className="p-6 text-sm text-slate-500 dark:text-slate-400 text-center bg-white dark:bg-slate-900">
                Start typing to search your tickets.
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
